import React, { useState, useEffect } from 'react';
import './ManageDRP.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaPlus,
  FaTrash,
  FaEye,
  FaSearch,
  FaFilter,
  FaCheckCircle,
  FaClock,
  FaHospital,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaFileAlt
} from 'react-icons/fa';

const ManageDRP = () => {
  const [postings, setPostings] = useState([]);
  const [students, setStudents] = useState([]);
  const [drpcUsers, setDrpcUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [statistics, setStatistics] = useState(null);

  // Form state for creating posting
  const [formData, setFormData] = useState({
    studentId: '',
    district: {
      name: '',
      state: '',
      hospitalName: '',
      hospitalAddress: '',
      hospitalType: 'District Hospital'
    },
    postingDetails: {
      department: 'Medicine',
      startDate: '',
      endDate: '',
      semester: 'Semester 3',
      academicYear: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1)
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [postingsRes, studentsRes, drpcRes, statsRes] = await Promise.all([
        fetch(`${collegeConfig.api.baseURL}/drp/admin/postings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${collegeConfig.api.baseURL}/students`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${collegeConfig.api.baseURL}/users?role=drpc`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ ok: false })), // DRPC users might not exist yet
        fetch(`${collegeConfig.api.baseURL}/drp/admin/statistics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (postingsRes.ok) {
        const data = await postingsRes.json();
        setPostings(data.postings || []);
      }

      if (studentsRes.ok) {
        const data = await studentsRes.json();
        setStudents(data.students || []);
      }

      if (drpcRes.ok) {
        const data = await drpcRes.json();
        setDrpcUsers(data.users || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStatistics(data.statistics);
      }

    } catch (error) {
      console.error('Error fetching DRP data:', error);
      setError('Failed to load DRP data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePosting = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/drp/admin/posting`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('✅ DRP posting created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchData();
      } else {
        const data = await response.json();
        alert('❌ ' + (data.message || 'Failed to create posting'));
      }
    } catch (error) {
      console.error('Error creating posting:', error);
      alert('❌ Error creating posting');
    }
  };

  const handleDeletePosting = async (postingId) => {
    if (!window.confirm('Are you sure you want to delete this DRP posting?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/drp/admin/posting/${postingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('✅ Posting deleted successfully');
        fetchData();
      } else {
        alert('❌ Failed to delete posting');
      }
    } catch (error) {
      console.error('Error deleting posting:', error);
      alert('❌ Error deleting posting');
    }
  };

  const resetForm = () => {
    setFormData({
      studentId: '',
      district: {
        name: '',
        state: '',
        hospitalName: '',
        hospitalAddress: '',
        hospitalType: 'District Hospital'
      },
      postingDetails: {
        department: 'Medicine',
        startDate: '',
        endDate: '',
        semester: 'Semester 3',
        academicYear: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1)
      }
    });
  };

  const filteredPostings = postings.filter(posting => {
    const matchesSearch = 
      posting.student?.profile?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      posting.student?.profile?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      posting.district?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      posting.postingDetails?.department?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = 
      filterStatus === 'all' ||
      (filterStatus === 'active' && posting.isActive && !posting.isCompleted) ||
      (filterStatus === 'completed' && posting.isCompleted) ||
      (filterStatus === 'pending' && posting.verificationStatus === 'pending') ||
      (filterStatus === 'verified' && posting.verificationStatus !== 'pending' && posting.verificationStatus !== 'completed');

    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (posting) => {
    if (posting.isCompleted) {
      return <span className="statusBadge_5672 completed_5672"><FaCheckCircle /> Completed</span>;
    }
    if (posting.verificationStatus === 'pending') {
      return <span className="statusBadge_5672 pending_5672"><FaClock /> Pending</span>;
    }
    if (posting.verificationStatus === 'drpc_verified') {
      return <span className="statusBadge_5672 drpcVerified_5672"><FaCheckCircle /> DRPC Verified</span>;
    }
    if (posting.verificationStatus === 'teacher_verified') {
      return <span className="statusBadge_5672 teacherVerified_5672"><FaCheckCircle /> Teacher Verified</span>;
    }
    return <span className="statusBadge_5672 active_5672"><FaClock /> Active</span>;
  };

  return (
    <div className="manageDrpContainer_5672">
      {/* Header with Statistics */}
      <div className="drpHeader_5672">
        <h2>District Residency Programme Management</h2>
        <button className="btnPrimary_5672" onClick={() => setShowCreateModal(true)}>
          <FaPlus /> Create DRP Posting
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="drpStatsGrid_5672">
          <div className="statCard_5672">
            <div className="statIcon_5672" style={{ background: '#3498DB' }}>
              <FaHospital />
            </div>
            <div className="statContent_5672">
              <h3>{statistics.totalPostings}</h3>
              <p>Total Postings</p>
            </div>
          </div>
          
          <div className="statCard_5672">
            <div className="statIcon_5672" style={{ background: '#2ECC71' }}>
              <FaCheckCircle />
            </div>
            <div className="statContent_5672">
              <h3>{statistics.activePostings}</h3>
              <p>Active Postings</p>
            </div>
          </div>
          
          <div className="statCard_5672">
            <div className="statIcon_5672" style={{ background: '#F39C12' }}>
              <FaFileAlt />
            </div>
            <div className="statContent_5672">
              <h3>{statistics.completedPostings}</h3>
              <p>Completed</p>
            </div>
          </div>
          
          <div className="statCard_5672">
            <div className="statIcon_5672" style={{ background: '#E74C3C' }}>
              <FaClock />
            </div>
            <div className="statContent_5672">
              <h3>{statistics.pendingVerification}</h3>
              <p>Pending Verification</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="drpControls_5672">
        <div className="searchBox_5672">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by student, district, or department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filterBox_5672">
          <FaFilter />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Postings</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending Verification</option>
            <option value="verified">Verified</option>
          </select>
        </div>
      </div>

      {/* Postings Table */}
      {loading ? (
        <div className="loadingState_5672">
          <div className="spinner_5672"></div>
          <p>Loading DRP postings...</p>
        </div>
      ) : error ? (
        <div className="errorState_5672">{error}</div>
      ) : filteredPostings.length === 0 ? (
        <div className="emptyState_5672">
          <FaHospital size={60} color="#ccc" />
          <h3>No DRP Postings Found</h3>
          <p>Create your first DRP posting to get started</p>
          <button className="btnPrimary_5672" onClick={() => setShowCreateModal(true)}>
            <FaPlus /> Create Posting
          </button>
        </div>
      ) : (
        <div className="postingsTable_5672">
          <table>
            <thead>
              <tr>
                <th>Student</th>
                <th>District</th>
                <th>Department</th>
                <th>Duration</th>
                <th>Attendance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPostings.map((posting) => (
                <tr key={posting._id}>
                  <td>
                    <div className="studentInfo_5672">
                      <strong>
                        {posting.student?.profile?.firstName} {posting.student?.profile?.lastName}
                      </strong>
                      <small>{posting.student?.profile?.registrationNumber}</small>
                    </div>
                  </td>
                  <td>
                    <div className="districtInfo_5672">
                      <FaMapMarkerAlt /> {posting.district?.name}, {posting.district?.state}
                      <small>{posting.district?.hospitalName}</small>
                    </div>
                  </td>
                  <td>
                    <span className="departmentBadge_5672">{posting.postingDetails?.department}</span>
                  </td>
                  <td>
                    <div className="dateRange_5672">
                      <FaCalendarAlt />
                      <span>
                        {new Date(posting.postingDetails?.startDate).toLocaleDateString()} - 
                        {new Date(posting.postingDetails?.endDate).toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="attendanceInfo_5672">
                      {posting.attendanceStats?.attendancePercentage?.toFixed(1) || 0}%
                      <small>
                        {posting.attendanceStats?.presentDays || 0}/{posting.attendanceStats?.totalDays || 0} days
                      </small>
                    </div>
                  </td>
                  <td>{getStatusBadge(posting)}</td>
                  <td>
                    <div className="actionButtons_5672">
                      <button
                        className="btnIcon_5672"
                        title="View Details"
                        onClick={() => {
                          setSelectedPosting(posting);
                          setShowDetailsModal(true);
                        }}
                      >
                        <FaEye />
                      </button>
                      <button
                        className="btnIcon_5672 btnDanger_5672"
                        title="Delete"
                        onClick={() => handleDeletePosting(posting._id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Posting Modal */}
      {showCreateModal && (
        <div className="modalOverlay_5672" onClick={() => setShowCreateModal(false)}>
          <div className="modalContent_5672 large_5672" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader_5672">
              <h3>Create DRP Posting</h3>
              <button className="closeBtn_5672" onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            <form onSubmit={handleCreatePosting}>
              <div className="formSection_5672">
                <h4>Student Information</h4>
                <div className="formGroup_5672">
                  <label>Select Student *</label>
                  <select
                    required
                    value={formData.studentId}
                    onChange={(e) => setFormData({...formData, studentId: e.target.value})}
                  >
                    <option value="">-- Select Student --</option>
                    {students.map(student => (
                      <option key={student._id} value={student._id}>
                        {student.profile?.firstName} {student.profile?.lastName} ({student.profile?.registrationNumber})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="formSection_5672">
                <h4>District & Hospital Details</h4>
                <div className="formRow_5672">
                  <div className="formGroup_5672">
                    <label>District Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.district.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        district: {...formData.district, name: e.target.value}
                      })}
                      placeholder="e.g., Pune"
                    />
                  </div>
                  <div className="formGroup_5672">
                    <label>State *</label>
                    <input
                      type="text"
                      required
                      value={formData.district.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        district: {...formData.district, state: e.target.value}
                      })}
                      placeholder="e.g., Maharashtra"
                    />
                  </div>
                </div>

                <div className="formGroup_5672">
                  <label>Hospital Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.district.hospitalName}
                    onChange={(e) => setFormData({
                      ...formData,
                      district: {...formData.district, hospitalName: e.target.value}
                    })}
                    placeholder="e.g., District Hospital Pune"
                  />
                </div>

                <div className="formGroup_5672">
                  <label>Hospital Address</label>
                  <input
                    type="text"
                    value={formData.district.hospitalAddress}
                    onChange={(e) => setFormData({
                      ...formData,
                      district: {...formData.district, hospitalAddress: e.target.value}
                    })}
                    placeholder="Complete hospital address"
                  />
                </div>

                <div className="formGroup_5672">
                  <label>Hospital Type</label>
                  <select
                    value={formData.district.hospitalType}
                    onChange={(e) => setFormData({
                      ...formData,
                      district: {...formData.district, hospitalType: e.target.value}
                    })}
                  >
                    <option value="District Hospital">District Hospital</option>
                    <option value="CHC">CHC (Community Health Center)</option>
                    <option value="PHC">PHC (Primary Health Center)</option>
                    <option value="Sub-District Hospital">Sub-District Hospital</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="formSection_5672">
                <h4>Posting Details</h4>
                <div className="formRow_5672">
                  <div className="formGroup_5672">
                    <label>Department *</label>
                    <select
                      required
                      value={formData.postingDetails.department}
                      onChange={(e) => setFormData({
                        ...formData,
                        postingDetails: {...formData.postingDetails, department: e.target.value}
                      })}
                    >
                      <option value="Medicine">Medicine</option>
                      <option value="Surgery">Surgery</option>
                      <option value="Obstetrics & Gynecology">Obstetrics & Gynecology</option>
                      <option value="Pediatrics">Pediatrics</option>
                      <option value="Orthopedics">Orthopedics</option>
                      <option value="ENT">ENT</option>
                      <option value="Ophthalmology">Ophthalmology</option>
                      <option value="Anesthesia">Anesthesia</option>
                      <option value="Radiology">Radiology</option>
                      <option value="Pathology">Pathology</option>
                      <option value="Community Medicine">Community Medicine</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="formGroup_5672">
                    <label>Semester *</label>
                    <select
                      required
                      value={formData.postingDetails.semester}
                      onChange={(e) => setFormData({
                        ...formData,
                        postingDetails: {...formData.postingDetails, semester: e.target.value}
                      })}
                    >
                      <option value="Semester 1">Semester 1</option>
                      <option value="Semester 2">Semester 2</option>
                      <option value="Semester 3">Semester 3</option>
                      <option value="Semester 4">Semester 4</option>
                      <option value="Semester 5">Semester 5</option>
                      <option value="Semester 6">Semester 6</option>
                    </select>
                  </div>
                </div>

                <div className="formRow_5672">
                  <div className="formGroup_5672">
                    <label>Start Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.postingDetails.startDate}
                      onChange={(e) => setFormData({
                        ...formData,
                        postingDetails: {...formData.postingDetails, startDate: e.target.value}
                      })}
                    />
                  </div>

                  <div className="formGroup_5672">
                    <label>End Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.postingDetails.endDate}
                      onChange={(e) => setFormData({
                        ...formData,
                        postingDetails: {...formData.postingDetails, endDate: e.target.value}
                      })}
                    />
                  </div>
                </div>

                <div className="formGroup_5672">
                  <label>Academic Year</label>
                  <input
                    type="text"
                    value={formData.postingDetails.academicYear}
                    onChange={(e) => setFormData({
                      ...formData,
                      postingDetails: {...formData.postingDetails, academicYear: e.target.value}
                    })}
                    placeholder="e.g., 2024-2025"
                  />
                </div>
              </div>

              <div className="modalActions_5672">
                <button type="button" className="btnSecondary_5672" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btnPrimary_5672">
                  Create Posting
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedPosting && (
        <div className="modalOverlay_5672" onClick={() => setShowDetailsModal(false)}>
          <div className="modalContent_5672 large_5672" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader_5672">
              <h3>DRP Posting Details</h3>
              <button className="closeBtn_5672" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            <div className="postingDetails_5672">
              <div className="detailSection_5672">
                <h4>Student Information</h4>
                <p><strong>Name:</strong> {selectedPosting.student?.profile?.firstName} {selectedPosting.student?.profile?.lastName}</p>
                <p><strong>Registration No:</strong> {selectedPosting.student?.profile?.registrationNumber}</p>
                <p><strong>Email:</strong> {selectedPosting.student?.email}</p>
              </div>

              <div className="detailSection_5672">
                <h4>District & Hospital</h4>
                <p><strong>District:</strong> {selectedPosting.district?.name}, {selectedPosting.district?.state}</p>
                <p><strong>Hospital:</strong> {selectedPosting.district?.hospitalName}</p>
                <p><strong>Type:</strong> {selectedPosting.district?.hospitalType}</p>
              </div>

              <div className="detailSection_5672">
                <h4>Posting Details</h4>
                <p><strong>Department:</strong> {selectedPosting.postingDetails?.department}</p>
                <p><strong>Semester:</strong> {selectedPosting.postingDetails?.semester}</p>
                <p><strong>Duration:</strong> {new Date(selectedPosting.postingDetails?.startDate).toLocaleDateString()} - {new Date(selectedPosting.postingDetails?.endDate).toLocaleDateString()}</p>
                <p><strong>Total Days:</strong> {selectedPosting.postingDetails?.totalDays}</p>
              </div>

              <div className="detailSection_5672">
                <h4>Progress Statistics</h4>
                <p><strong>Attendance:</strong> {selectedPosting.attendanceStats?.attendancePercentage?.toFixed(1)}% ({selectedPosting.attendanceStats?.presentDays}/{selectedPosting.attendanceStats?.totalDays} days)</p>
                <p><strong>Daily Activities:</strong> {selectedPosting.dailyActivities?.length || 0} entries</p>
                <p><strong>Weekly Reports:</strong> {selectedPosting.weeklyReports?.length || 0} submitted</p>
                <p><strong>Status:</strong> {getStatusBadge(selectedPosting)}</p>
              </div>

              {selectedPosting.performance?.finalScore && (
                <div className="detailSection_5672">
                  <h4>Performance Evaluation</h4>
                  <p><strong>Final Score:</strong> {selectedPosting.performance.finalScore}/100</p>
                  <p><strong>Grade:</strong> {selectedPosting.performance.grade}</p>
                  {selectedPosting.performance.drpcScore && (
                    <p><strong>DRPC Score:</strong> {selectedPosting.performance.drpcScore}/100</p>
                  )}
                  {selectedPosting.performance.teacherScore && (
                    <p><strong>Teacher Score:</strong> {selectedPosting.performance.teacherScore}/100</p>
                  )}
                </div>
              )}
            </div>

            <div className="modalActions_5672">
              <button className="btnSecondary_5672" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDRP;